import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText, ArrowLeft, Plus, Edit2, Trash2, Eye, Star, Check, X, Loader2,
  Palette, Type, Layout, Settings2, Image, AlignLeft, AlignCenter, AlignRight,
  Copy, MoreHorizontal
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

const DEFAULT_TEMPLATE = {
  name: "",
  layout: "standard",
  logo_url: "",
  logo_position: "top-left",
  logo_width: 150,
  logo_height: 50,
  primary_color: "#0d9488",
  secondary_color: "#374151",
  accent_color: "#f9fafb",
  text_color: "#1f2937",
  font_family: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
  header_font_size: 28,
  body_font_size: 14,
  show_logo: true,
  show_company_info: true,
  show_company_email: true,
  show_company_phone: true,
  show_company_address: true,
  show_client_email: true,
  show_client_phone: true,
  show_client_address: true,
  show_client_tax_id: true,
  show_po_number: true,
  show_balance_due: true,
  show_due_date: true,
  show_tax_breakdown: true,
  show_discount: true,
  show_notes: true,
  show_terms: true,
  show_footer: true,
  show_status_badge: true,
  footer_text: "Thank you for your business!",
  footer_company_name: "",
  footer_address: "",
  custom_html: ""
};

const FONT_OPTIONS = [
  { value: "Segoe UI, Tahoma, Geneva, Verdana, sans-serif", label: "Segoe UI" },
  { value: "Arial, Helvetica, sans-serif", label: "Arial" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "Times New Roman, serif", label: "Times New Roman" },
  { value: "Courier New, monospace", label: "Courier New" },
  { value: "Verdana, sans-serif", label: "Verdana" },
  { value: "Roboto, sans-serif", label: "Roboto" },
  { value: "Open Sans, sans-serif", label: "Open Sans" },
];

const LAYOUT_OPTIONS = [
  { value: "standard", label: "Standard", description: "Classic professional layout" },
  { value: "minimal", label: "Minimal", description: "Clean and simple design" },
  { value: "detailed", label: "Detailed", description: "Comprehensive with all fields" },
  { value: "custom", label: "Custom HTML", description: "Advanced: Write your own HTML" },
];

export default function InvoiceTemplatesPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  // Dialog states
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  
  // Form state
  const [formData, setFormData] = useState({ ...DEFAULT_TEMPLATE });
  const [activeTab, setActiveTab] = useState("basic");

  const fetchTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await api.get("/invoice-templates");
      setTemplates(response.data || []);
    } catch (error) {
      console.error("Error fetching templates:", error);
      toast.error(t("failed_load_data", "common") || "Failed to load templates");
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleCreate = () => {
    setSelectedTemplate(null);
    setFormData({ ...DEFAULT_TEMPLATE });
    setActiveTab("basic");
    setShowCreateDialog(true);
  };

  const handleEdit = (template) => {
    setSelectedTemplate(template);
    setFormData({
      name: template.name || "",
      layout: template.layout || "standard",
      logo_url: template.logo_url || "",
      logo_position: template.logo_position || "top-left",
      logo_width: template.logo_width || 150,
      logo_height: template.logo_height || 50,
      primary_color: template.primary_color || "#0d9488",
      secondary_color: template.secondary_color || "#374151",
      accent_color: template.accent_color || "#f9fafb",
      text_color: template.text_color || "#1f2937",
      font_family: template.font_family || "Segoe UI, Tahoma, Geneva, Verdana, sans-serif",
      header_font_size: template.header_font_size || 28,
      body_font_size: template.body_font_size || 14,
      show_logo: template.show_logo ?? true,
      show_company_info: template.show_company_info ?? true,
      show_company_email: template.show_company_email ?? true,
      show_company_phone: template.show_company_phone ?? true,
      show_company_address: template.show_company_address ?? true,
      show_client_email: template.show_client_email ?? true,
      show_client_phone: template.show_client_phone ?? true,
      show_client_address: template.show_client_address ?? true,
      show_client_tax_id: template.show_client_tax_id ?? true,
      show_po_number: template.show_po_number ?? true,
      show_balance_due: template.show_balance_due ?? true,
      show_due_date: template.show_due_date ?? true,
      show_tax_breakdown: template.show_tax_breakdown ?? true,
      show_discount: template.show_discount ?? true,
      show_notes: template.show_notes ?? true,
      show_terms: template.show_terms ?? true,
      show_footer: template.show_footer ?? true,
      show_status_badge: template.show_status_badge ?? true,
      footer_text: template.footer_text || "",
      footer_company_name: template.footer_company_name || "",
      footer_address: template.footer_address || "",
      custom_html: template.custom_html || ""
    });
    setActiveTab("basic");
    setShowCreateDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error(t("name_required", "common") || "Template name is required");
      return;
    }

    setIsSaving(true);
    try {
      if (selectedTemplate) {
        await api.put(`/invoice-templates/${selectedTemplate.id}`, formData);
        toast.success(t("template_updated", "invoice_templates") || "Template updated successfully");
      } else {
        await api.post("/invoice-templates", formData);
        toast.success(t("template_created", "invoice_templates") || "Template created successfully");
      }
      setShowCreateDialog(false);
      fetchTemplates();
    } catch (error) {
      console.error("Error saving template:", error);
      toast.error(t("error_saving", "common") || "Error saving template");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedTemplate) return;
    
    setIsSaving(true);
    try {
      await api.delete(`/invoice-templates/${selectedTemplate.id}`);
      toast.success(t("template_deleted", "invoice_templates") || "Template deleted successfully");
      setShowDeleteDialog(false);
      setSelectedTemplate(null);
      fetchTemplates();
    } catch (error) {
      console.error("Error deleting template:", error);
      toast.error(t("error_deleting", "common") || "Error deleting template");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSetDefault = async (template) => {
    try {
      await api.post(`/invoice-templates/${template.id}/set-default`);
      toast.success(t("set_as_default_success", "invoice_templates") || "Template set as default");
      fetchTemplates();
    } catch (error) {
      console.error("Error setting default:", error);
      toast.error(t("error_setting_default", "invoice_templates") || "Error setting as default");
    }
  };

  const handlePreview = async (template) => {
    try {
      const response = await api.post(`/invoice-templates/${template.id}/preview`);
      setPreviewHtml(response.data);
      setSelectedTemplate(template);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error("Error previewing template:", error);
      toast.error(t("error_previewing", "invoice_templates") || "Error loading preview");
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const duplicateData = {
        ...template,
        name: `${template.name} (Copy)`,
      };
      delete duplicateData.id;
      delete duplicateData.user_id;
      delete duplicateData.is_default;
      delete duplicateData.created_at;
      delete duplicateData.updated_at;
      
      await api.post("/invoice-templates", duplicateData);
      toast.success(t("template_duplicated", "invoice_templates") || "Template duplicated");
      fetchTemplates();
    } catch (error) {
      console.error("Error duplicating template:", error);
      toast.error(t("error_duplicating", "invoice_templates") || "Error duplicating template");
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Skeleton className="h-10 w-10" />
          <div>
            <Skeleton className="h-8 w-48 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="page-title">
              {t("invoice_templates", "invoice_templates") || "Invoice Templates"}
            </h1>
            <p className="text-muted-foreground">
              {t("invoice_templates_desc", "invoice_templates") || "Create and manage custom invoice templates"}
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} data-testid="create-template-btn">
          <Plus className="h-4 w-4 mr-2" />
          {t("create_template", "invoice_templates") || "Create Template"}
        </Button>
      </div>

      {/* Templates Grid */}
      {templates.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-lg font-semibold mb-2">
              {t("no_templates", "invoice_templates") || "No Templates Yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {t("no_templates_desc", "invoice_templates") || "Create your first invoice template to customize how your invoices look."}
            </p>
            <Button onClick={handleCreate}>
              <Plus className="h-4 w-4 mr-2" />
              {t("create_first_template", "invoice_templates") || "Create Your First Template"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((template) => (
            <Card 
              key={template.id} 
              className={`relative hover:shadow-md transition-shadow ${template.is_default ? 'ring-2 ring-primary' : ''}`}
              data-testid={`template-card-${template.id}`}
            >
              {template.is_default && (
                <Badge className="absolute top-3 right-3 bg-primary">
                  <Star className="h-3 w-3 mr-1" />
                  {t("default", "common") || "Default"}
                </Badge>
              )}
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <div 
                    className="w-4 h-4 rounded-full" 
                    style={{ backgroundColor: template.primary_color }}
                  />
                  {template.name}
                </CardTitle>
                <CardDescription>
                  {LAYOUT_OPTIONS.find(l => l.value === template.layout)?.label || "Standard"} Layout
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1 mb-4">
                  {template.show_logo && <Badge variant="outline" className="text-xs">Logo</Badge>}
                  {template.show_po_number && <Badge variant="outline" className="text-xs">PO#</Badge>}
                  {template.show_balance_due && <Badge variant="outline" className="text-xs">Balance</Badge>}
                  {template.show_tax_breakdown && <Badge variant="outline" className="text-xs">Tax</Badge>}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handlePreview(template)}>
                    <Eye className="h-4 w-4 mr-1" />
                    {t("preview", "common") || "Preview"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(template)}>
                    <Edit2 className="h-4 w-4 mr-1" />
                    {t("edit", "common") || "Edit"}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!template.is_default && (
                        <DropdownMenuItem onClick={() => handleSetDefault(template)}>
                          <Star className="h-4 w-4 mr-2" />
                          {t("set_as_default", "invoice_templates") || "Set as Default"}
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => handleDuplicate(template)}>
                        <Copy className="h-4 w-4 mr-2" />
                        {t("duplicate", "common") || "Duplicate"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem 
                        className="text-destructive"
                        onClick={() => {
                          setSelectedTemplate(template);
                          setShowDeleteDialog(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t("delete", "common") || "Delete"}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {selectedTemplate 
                ? (t("edit_template", "invoice_templates") || "Edit Template")
                : (t("create_template", "invoice_templates") || "Create Template")
              }
            </DialogTitle>
            <DialogDescription>
              {t("template_form_desc", "invoice_templates") || "Customize how your invoices will look"}
            </DialogDescription>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid grid-cols-5 w-full">
              <TabsTrigger value="basic">
                <Settings2 className="h-4 w-4 mr-1" />
                {t("basic", "common") || "Basic"}
              </TabsTrigger>
              <TabsTrigger value="branding">
                <Palette className="h-4 w-4 mr-1" />
                {t("branding", "invoice_templates") || "Branding"}
              </TabsTrigger>
              <TabsTrigger value="typography">
                <Type className="h-4 w-4 mr-1" />
                {t("typography", "invoice_templates") || "Typography"}
              </TabsTrigger>
              <TabsTrigger value="sections">
                <Layout className="h-4 w-4 mr-1" />
                {t("sections", "invoice_templates") || "Sections"}
              </TabsTrigger>
              <TabsTrigger value="footer">
                <AlignLeft className="h-4 w-4 mr-1" />
                {t("footer", "invoice_templates") || "Footer"}
              </TabsTrigger>
            </TabsList>

            <ScrollArea className="flex-1 mt-4">
              <div className="pr-4">
                {/* Basic Tab */}
                <TabsContent value="basic" className="space-y-4 mt-0">
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="name">{t("template_name", "invoice_templates") || "Template Name"} *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={t("template_name_placeholder", "invoice_templates") || "e.g., Professional Blue"}
                        data-testid="template-name-input"
                      />
                    </div>
                    
                    <div className="grid gap-2">
                      <Label>{t("layout", "invoice_templates") || "Layout"}</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {LAYOUT_OPTIONS.map((option) => (
                          <Card 
                            key={option.value}
                            className={`cursor-pointer transition-all hover:border-primary ${formData.layout === option.value ? 'border-primary bg-primary/5' : ''}`}
                            onClick={() => setFormData({ ...formData, layout: option.value })}
                          >
                            <CardContent className="p-3">
                              <div className="font-medium">{option.label}</div>
                              <div className="text-xs text-muted-foreground">{option.description}</div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Branding Tab */}
                <TabsContent value="branding" className="space-y-4 mt-0">
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="logo_url">{t("logo_url", "invoice_templates") || "Logo URL"}</Label>
                      <Input
                        id="logo_url"
                        value={formData.logo_url}
                        onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                        placeholder="https://example.com/logo.png"
                        data-testid="logo-url-input"
                      />
                      {formData.logo_url && (
                        <div className="mt-2 p-4 border rounded-md bg-muted/50">
                          <img 
                            src={formData.logo_url} 
                            alt="Logo preview" 
                            style={{ maxWidth: formData.logo_width, maxHeight: formData.logo_height }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label>{t("logo_position", "invoice_templates") || "Logo Position"}</Label>
                        <Select
                          value={formData.logo_position}
                          onValueChange={(value) => setFormData({ ...formData, logo_position: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top-left">
                              <div className="flex items-center gap-2">
                                <AlignLeft className="h-4 w-4" /> Top Left
                              </div>
                            </SelectItem>
                            <SelectItem value="top-center">
                              <div className="flex items-center gap-2">
                                <AlignCenter className="h-4 w-4" /> Top Center
                              </div>
                            </SelectItem>
                            <SelectItem value="top-right">
                              <div className="flex items-center gap-2">
                                <AlignRight className="h-4 w-4" /> Top Right
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="grid gap-2">
                        <Label>{t("logo_size", "invoice_templates") || "Logo Size"}</Label>
                        <div className="flex gap-2">
                          <Input
                            type="number"
                            value={formData.logo_width}
                            onChange={(e) => setFormData({ ...formData, logo_width: parseInt(e.target.value) || 150 })}
                            placeholder="Width"
                            className="w-24"
                          />
                          <span className="self-center">×</span>
                          <Input
                            type="number"
                            value={formData.logo_height}
                            onChange={(e) => setFormData({ ...formData, logo_height: parseInt(e.target.value) || 50 })}
                            placeholder="Height"
                            className="w-24"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label>{t("primary_color", "invoice_templates") || "Primary Color"}</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={formData.primary_color}
                            onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                            className="w-12 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={formData.primary_color}
                            onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                            placeholder="#0d9488"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      
                      <div className="grid gap-2">
                        <Label>{t("secondary_color", "invoice_templates") || "Secondary Color"}</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={formData.secondary_color}
                            onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                            className="w-12 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={formData.secondary_color}
                            onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                            placeholder="#374151"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label>{t("accent_color", "invoice_templates") || "Accent Color"}</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={formData.accent_color}
                            onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                            className="w-12 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={formData.accent_color}
                            onChange={(e) => setFormData({ ...formData, accent_color: e.target.value })}
                            placeholder="#f9fafb"
                            className="flex-1"
                          />
                        </div>
                      </div>
                      
                      <div className="grid gap-2">
                        <Label>{t("text_color", "invoice_templates") || "Text Color"}</Label>
                        <div className="flex gap-2">
                          <Input
                            type="color"
                            value={formData.text_color}
                            onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                            className="w-12 h-10 p-1 cursor-pointer"
                          />
                          <Input
                            value={formData.text_color}
                            onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
                            placeholder="#1f2937"
                            className="flex-1"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Typography Tab */}
                <TabsContent value="typography" className="space-y-4 mt-0">
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <Label>{t("font_family", "invoice_templates") || "Font Family"}</Label>
                      <Select
                        value={formData.font_family}
                        onValueChange={(value) => setFormData({ ...formData, font_family: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONT_OPTIONS.map((font) => (
                            <SelectItem key={font.value} value={font.value}>
                              <span style={{ fontFamily: font.value }}>{font.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="grid gap-2">
                        <Label>{t("header_font_size", "invoice_templates") || "Header Font Size"}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={formData.header_font_size}
                            onChange={(e) => setFormData({ ...formData, header_font_size: parseInt(e.target.value) || 28 })}
                            min={16}
                            max={48}
                            className="w-24"
                          />
                          <span className="text-muted-foreground">px</span>
                        </div>
                      </div>
                      
                      <div className="grid gap-2">
                        <Label>{t("body_font_size", "invoice_templates") || "Body Font Size"}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={formData.body_font_size}
                            onChange={(e) => setFormData({ ...formData, body_font_size: parseInt(e.target.value) || 14 })}
                            min={10}
                            max={24}
                            className="w-24"
                          />
                          <span className="text-muted-foreground">px</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                {/* Sections Tab */}
                <TabsContent value="sections" className="space-y-4 mt-0">
                  <p className="text-sm text-muted-foreground mb-4">
                    {t("sections_desc", "invoice_templates") || "Choose which sections to display on your invoices"}
                  </p>
                  
                  {/* General Sections */}
                  <div className="space-y-2">
                    <h4 className="text-sm font-semibold">{t("general_sections", "invoice_templates") || "General Sections"}</h4>
                    <div className="grid gap-3">
                      {[
                        { key: "show_logo", label: t("show_logo", "invoice_templates") || "Show Logo" },
                        { key: "show_status_badge", label: t("show_status_badge", "invoice_templates") || "Show Status Badge" },
                        { key: "show_po_number", label: t("show_po_number", "invoice_templates") || "Show P.O. Number" },
                        { key: "show_balance_due", label: t("show_balance_due", "invoice_templates") || "Show Balance Due" },
                        { key: "show_due_date", label: t("show_due_date", "invoice_templates") || "Show Due Date" },
                        { key: "show_tax_breakdown", label: t("show_tax_breakdown", "invoice_templates") || "Show Tax Breakdown" },
                        { key: "show_discount", label: t("show_discount", "invoice_templates") || "Show Discount" },
                        { key: "show_notes", label: t("show_notes", "invoice_templates") || "Show Notes Section" },
                        { key: "show_terms", label: t("show_terms", "invoice_templates") || "Show Terms & Conditions" },
                        { key: "show_footer", label: t("show_footer", "invoice_templates") || "Show Footer" },
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between p-3 border rounded-lg">
                          <Label htmlFor={item.key} className="cursor-pointer">{item.label}</Label>
                          <Switch
                            id={item.key}
                            checked={formData[item.key]}
                            onCheckedChange={(checked) => setFormData({ ...formData, [item.key]: checked })}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Company Info Fields */}
                  <div className="space-y-2 mt-6">
                    <h4 className="text-sm font-semibold">{t("company_info_fields", "invoice_templates") || "Company Info Fields"}</h4>
                    <div className="grid gap-3">
                      <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                        <Label htmlFor="show_company_info" className="cursor-pointer font-medium">
                          {t("show_company_info", "invoice_templates") || "Show Company Info (Master Toggle)"}
                        </Label>
                        <Switch
                          id="show_company_info"
                          checked={formData.show_company_info}
                          onCheckedChange={(checked) => setFormData({ ...formData, show_company_info: checked })}
                        />
                      </div>
                      {formData.show_company_info && (
                        <>
                          {[
                            { key: "show_company_email", label: t("show_company_email", "invoice_templates") || "Show Company Email" },
                            { key: "show_company_phone", label: t("show_company_phone", "invoice_templates") || "Show Company Phone" },
                            { key: "show_company_address", label: t("show_company_address", "invoice_templates") || "Show Company Address" },
                          ].map((item) => (
                            <div key={item.key} className="flex items-center justify-between p-3 border rounded-lg ml-4">
                              <Label htmlFor={item.key} className="cursor-pointer">{item.label}</Label>
                              <Switch
                                id={item.key}
                                checked={formData[item.key]}
                                onCheckedChange={(checked) => setFormData({ ...formData, [item.key]: checked })}
                              />
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Client Info Fields */}
                  <div className="space-y-2 mt-6">
                    <h4 className="text-sm font-semibold">{t("client_info_fields", "invoice_templates") || "Client Info Fields"}</h4>
                    <div className="grid gap-3">
                      {[
                        { key: "show_client_email", label: t("show_client_email", "invoice_templates") || "Show Client Email" },
                        { key: "show_client_phone", label: t("show_client_phone", "invoice_templates") || "Show Client Phone" },
                        { key: "show_client_address", label: t("show_client_address", "invoice_templates") || "Show Client Address" },
                        { key: "show_client_tax_id", label: t("show_client_tax_id", "invoice_templates") || "Show Client Tax ID" },
                      ].map((item) => (
                        <div key={item.key} className="flex items-center justify-between p-3 border rounded-lg">
                          <Label htmlFor={item.key} className="cursor-pointer">{item.label}</Label>
                          <Switch
                            id={item.key}
                            checked={formData[item.key]}
                            onCheckedChange={(checked) => setFormData({ ...formData, [item.key]: checked })}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* Footer Tab */}
                <TabsContent value="footer" className="space-y-4 mt-0">
                  <div className="grid gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="footer_text">{t("footer_text", "invoice_templates") || "Footer Text"}</Label>
                      <Input
                        id="footer_text"
                        value={formData.footer_text}
                        onChange={(e) => setFormData({ ...formData, footer_text: e.target.value })}
                        placeholder={t("footer_text_placeholder", "invoice_templates") || "Thank you for your business!"}
                      />
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="footer_company_name">{t("footer_company_name", "invoice_templates") || "Company Name (Footer)"}</Label>
                      <Input
                        id="footer_company_name"
                        value={formData.footer_company_name}
                        onChange={(e) => setFormData({ ...formData, footer_company_name: e.target.value })}
                        placeholder={t("footer_company_placeholder", "invoice_templates") || "Your Company Name"}
                      />
                    </div>
                    
                    <div className="grid gap-2">
                      <Label htmlFor="footer_address">{t("footer_address", "invoice_templates") || "Company Address (Footer)"}</Label>
                      <Textarea
                        id="footer_address"
                        value={formData.footer_address}
                        onChange={(e) => setFormData({ ...formData, footer_address: e.target.value })}
                        placeholder={t("footer_address_placeholder", "invoice_templates") || "123 Business St, City, State 12345"}
                        rows={2}
                      />
                    </div>

                    {formData.layout === "custom" && (
                      <div className="grid gap-2">
                        <Label htmlFor="custom_html">
                          {t("custom_html", "invoice_templates") || "Custom HTML Template"}
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          {t("custom_html_desc", "invoice_templates") || "Use placeholders like {{invoice_number}}, {{client_name}}, {{items_table}}, {{total}}, etc."}
                        </p>
                        <Textarea
                          id="custom_html"
                          value={formData.custom_html}
                          onChange={(e) => setFormData({ ...formData, custom_html: e.target.value })}
                          placeholder="<!DOCTYPE html>..."
                          rows={10}
                          className="font-mono text-sm"
                        />
                      </div>
                    )}
                  </div>
                </TabsContent>
              </div>
            </ScrollArea>
          </Tabs>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              {t("cancel", "common") || "Cancel"}
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedTemplate 
                ? (t("save_changes", "common") || "Save Changes")
                : (t("create", "common") || "Create")
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_template", "invoice_templates") || "Delete Template"}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_template_warning", "invoice_templates") || "Are you sure you want to delete this template? This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel", "common") || "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {t("delete", "common") || "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>
              {t("preview", "common") || "Preview"} - {selectedTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-hidden">
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[600px]"
              title="Invoice Preview"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              {t("close", "common") || "Close"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
